import jsPDF from "jspdf";
import "jspdf-autotable";

// Récupération des variables d'environnement
const PHARMACY_NAME = process.env.REACT_APP_PHARMACY_NAME;
const OWNER_NAME = process.env.REACT_APP_OWNER_NAME;
const ADDRESS_LINE1 = process.env.REACT_APP_ADDRESS_LINE1;
const ADDRESS_LINE2 = process.env.REACT_APP_ADDRESS_LINE2;
const BUSINESS_INFO = process.env.REACT_APP_BUSINESS_INFO;
const ADDRESS_EMAIL = process.env.REACT_APP_ADDRESS_EMAIL || "";

// Fonction pour obtenir les variables TVA dynamiquement
const getTVAConfig = () => {
  const tvaEnabled = process.env.REACT_APP_FEATURE_FLIPPING_TVA === 'true';
  const tvaMobile = parseFloat(process.env.REACT_APP_FEATURE_FLIPPING_MOBILE?.replace(',', '.') || '0.005');
  const tvaEspece = parseFloat(process.env.REACT_APP_FEATURE_FLIPPING_ESPECE?.replace(',', '.') || '0.01');
  
  return {
    FEATURE_TVA_ENABLED: tvaEnabled,
    TVA_RATE_MOBILE: tvaMobile,
    TVA_RATE_ESPECE: tvaEspece
  };
};

// Initialiser la config TVA
let TVA_CONFIG = getTVAConfig();

// =============================================
// FONCTIONS DE FORMATAGE
// =============================================

/**
 * Formatage des prix avec décimales
 */
const formatPrice = (price) => {
  if (!price && price !== 0) return '0,00 Fcfa';
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  const formattedPrice = numericPrice.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ').replace('.', ',');
  return `${formattedPrice} Fcfa`;
};

/**
 * Formatage des prix SANS décimales
 */
const formatPriceNoDecimal = (price) => {
  if (!price && price !== 0) return '0 Fcfa';
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  const roundedPrice = Math.round(numericPrice);
  const formattedPrice = roundedPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formattedPrice} Fcfa`;
};

/**
 * Formatage de la date
 */
const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    return dateString;
  }
};

/**
 * Conversion du montant en lettres
 */
const amountToWords = (amount) => {
  const localAmount = Math.round(amount);
  const units = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];
  const teens = ['dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
  const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];
  
  if (localAmount === 0) return 'zéro';
  if (localAmount > 999999) return 'Montant trop élevé';

  const convertNumber = (num) => {
    let result = '';
    const thousands = Math.floor(num / 1000);
    if (thousands > 0) {
      if (thousands === 1) {
        result += 'mille ';
      } else {
        result += convertHundreds(thousands) + 'mille ';
      }
      num %= 1000;
    }
    if (num > 0) {
      result += convertHundreds(num);
    }
    return result.trim();
  };

  function convertHundreds(num) {
    let result = '';
    const hundreds = Math.floor(num / 100);
    if (hundreds > 0) {
      if (hundreds === 1) {
        result += 'cent ';
      } else {
        result += units[hundreds] + ' cent ';
      }
      num %= 100;
    }
    if (num > 0) {
      if (result !== '') result += '';
      if (num < 10) {
        result += units[num];
      } else if (num < 20) {
        result += teens[num - 10];
      } else {
        const ten = Math.floor(num / 10);
        const unit = num % 10;
        if (ten === 7 || ten === 9) {
          result += tens[ten] + '-' + teens[unit];
        } else {
          result += tens[ten];
          if (unit > 0) {
            if (ten === 8) {
              result += '-' + units[unit];
            } else {
              result += (ten === 1 ? ' et ' : '-') + units[unit];
            }
          }
        }
      }
    }
    return result;
  }

  return convertNumber(localAmount) + ' Francs CFA';
};

/**
 * Formatage du taux de TVA
 */
const formatTauxTVA = (modePaiement) => {
  const { FEATURE_TVA_ENABLED, TVA_RATE_MOBILE, TVA_RATE_ESPECE } = TVA_CONFIG;
  if (!FEATURE_TVA_ENABLED) return '0%';
  
  switch(modePaiement) {
    case 'ESPECE':
      return `${(TVA_RATE_ESPECE * 100).toFixed(1)}%`;
    case 'MOBILE':
    case 'OM':
    case 'WAVE':
      return `${(TVA_RATE_MOBILE * 100).toFixed(1)}%`;
    default:
      return '0%';
  }
};

// =============================================
// FONCTIONS DE CALCUL - CORRIGÉES
// =============================================

/**
 * ✅ Calcule le prix unitaire selon le mode TVA
 */
const calculerPrixUnitaire = (prixHT, modePaiement, remisePourcentage = 0) => {
  const { FEATURE_TVA_ENABLED, TVA_RATE_MOBILE, TVA_RATE_ESPECE } = TVA_CONFIG;
  
  // Calcul de la remise
  const remiseMontant = prixHT * (remisePourcentage / 100);
  const prixNetHT = prixHT - remiseMontant;
  
  if (!FEATURE_TVA_ENABLED) {
    return prixNetHT; // Retourne le prix HT
  }
  
  // TVA activée - calcul du prix TTC
  let tauxTVA = 0;
  if (modePaiement === 'ESPECE') {
    tauxTVA = TVA_RATE_ESPECE;
  } else if (modePaiement === 'MOBILE' || modePaiement === 'OM' || modePaiement === 'WAVE') {
    tauxTVA = TVA_RATE_MOBILE;
  }
  
  return prixNetHT * (1 + tauxTVA);
};

/**
 * ✅ Calcule le total d'une ligne selon le mode TVA
 */
const calculerTotalLigne = (prixHT, quantite, modePaiement, remisePourcentage = 0) => {
  const prixUnitaire = calculerPrixUnitaire(prixHT, modePaiement, remisePourcentage);
  return prixUnitaire * quantite;
};

/**
 * ✅ Calcule la TVA pour le récapitulatif (uniquement si TVA activée)
 */
const calculerTVA = (montantHT, modePaiement, remisePourcentage = 0) => {
  const { FEATURE_TVA_ENABLED, TVA_RATE_MOBILE, TVA_RATE_ESPECE } = TVA_CONFIG;
  
  if (!FEATURE_TVA_ENABLED) {
    return {
      montantHT: montantHT,
      tva: 0,
      tauxTVA: 0,
      ttc: montantHT,
      netSansTVA: montantHT,
      remiseMontant: 0
    };
  }

  const remiseMontant = montantHT * (remisePourcentage / 100);
  const netSansTVA = montantHT - remiseMontant;

  let tauxTVA = 0;
  if (modePaiement === 'ESPECE') {
    tauxTVA = TVA_RATE_ESPECE;
  } else if (modePaiement === 'MOBILE' || modePaiement === 'OM' || modePaiement === 'WAVE') {
    tauxTVA = TVA_RATE_MOBILE;
  }

  const tva = netSansTVA * tauxTVA;
  const ttc = netSansTVA + tva;

  return {
    montantHT: montantHT,
    remiseMontant: remiseMontant,
    netSansTVA: netSansTVA,
    tva: tva,
    tauxTVA: tauxTVA * 100,
    ttc: ttc
  };
};

// =============================================
// FONCTIONS DE GROUPE ET UTILITAIRES
// =============================================

/**
 * Grouper les produits par référence et créateur
 */
const groupProductsByReference = (reportProd) => {
  const groups = {};
  reportProd.forEach(prod => {
    const key = `${prod.reference || ''}_${prod.createur || ''}`;
    if (!groups[key]) {
      groups[key] = { reference: prod.reference, createur: prod.createur, produits: [] };
    }
    groups[key].produits.push(prod);
  });
  return Object.values(groups);
};

// =============================================
// EN-TÊTE MODERNE
// =============================================

/**
 * Créer l'en-tête de la facture
 */
const createModernHeader = (doc, reportProd, clientInfo) => {
  const blackColor = [0, 0, 0];
  const { FEATURE_TVA_ENABLED } = TVA_CONFIG;
  
  let totalHT = 0, total = 0, total_client = 0, total_ipm = 0, ipm = 0, client = 0, remise = 0;
  let numero = '', nom = '', telephone = '', adresse = '', statut = '', vendeur = '', modePaiement = 'ESPECE';
  
  const isIPMType = clientInfo?.designation?.toUpperCase() === "IPM RIDWAN";

  if (!isIPMType) {
    reportProd.forEach(prod => {
      const prixHT = prod.prix_sans_tva || prod.prixProdVendu || 0;
      const quantite = prod.qtVdu || 1;
      const montantHT = prixHT * quantite;
      const mode = prod.mode_payement || prod.mode || 'ESPECE';
      const remiseProd = prod.remise || 0;
      
      totalHT += montantHT;
      
      // ✅ Calcul du total selon le mode TVA
      if (FEATURE_TVA_ENABLED) {
        total += calculerTotalLigne(prixHT, quantite, mode, remiseProd);
      } else {
        total += montantHT;
      }
      
      remise = prod.remise;
      numero = prod.numFactSrt;
      nom = prod.nom;
      telephone = prod.telephone;
      adresse = prod.adresse;
      vendeur = prod.vendeur;
      statut = prod.statut;
      modePaiement = prod.mode_payement || prod.mode || modePaiement;
    });
  } else {
    reportProd.forEach(prod => {
      totalHT += prod.prix_sans_tva || prod.prixProdVendu || 0;
      total += prod.total || 0;
      client = client + prod.total_client;
      ipm = ipm + prod.total_ipm;
      numero = prod.numFactSrt;
      nom = prod.nom;
      telephone = prod.telephone;
      adresse = prod.adresse;
      vendeur = prod.vendeur;
      modePaiement = prod.mode_payement || prod.mode || modePaiement;
    });
  }

  // En-tête
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, 210, 45, 'F');
  
  doc.setFontSize(16);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'bold');
  doc.text(PHARMACY_NAME, 105, 18, { align: 'center' });
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(ADDRESS_LINE1, 105, 25, { align: 'center' });
  doc.text(ADDRESS_LINE2, 105, 30, { align: 'center' });
  if (BUSINESS_INFO) {
    doc.text(BUSINESS_INFO, 105, 35, { align: 'center' });
  }
  
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('FACTURE', 105, 45, { align: 'center' });
  
  const d = new Date();
  const datestring = ("0" + d.getDate()).slice(-2) + "/" + 
                     ("0" + (d.getMonth() + 1)).slice(-2) + "/" + 
                     d.getFullYear() + " • " + 
                     ("0" + d.getHours()).slice(-2) + ":" + 
                     ("0" + d.getMinutes()).slice(-2);
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`N°: ${numero}`, 15, 55);
  doc.text(`Date: ${datestring}`, 150, 55, { align: 'right' });
  
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(15, 60, 195, 60);
  
  doc.setFontSize(10);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'bold');
  doc.text('CLIENT', 15, 70);
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  
  let currentY = 77;
  doc.text(`Nom: ${nom || 'Non spécifié'}`, 20, currentY);
  doc.text(`Tél: ${telephone || 'Non spécifié'}`, 20, currentY + 6);
  if (vendeur && vendeur.toLowerCase() !== 'admin') {
    doc.text(`Vendeur: ${vendeur}`, 20, currentY + 12);
  }
  
  doc.text(`Adresse: ${adresse || 'Non spécifiée'}`, 110, currentY);
  if (statut) {
    doc.text(`Statut: ${statut}`, 110, currentY + 6);
  }
  
  currentY += 12;
  
  if (isIPMType) {
    doc.text(`Part Client: ${formatPrice(client)}`, 20, currentY);
    doc.text(`Part IPM: ${formatPrice(ipm)}`, 110, currentY);
    currentY += 6;
  } else {
    if (remise > 0) {
      doc.text(`Remise: ${remise}%`, 20, currentY);
    }
  }
  
  if (FEATURE_TVA_ENABLED) {
    doc.text(`Mode: ${modePaiement}`, 110, currentY);
    doc.text(`TVA: ${formatTauxTVA(modePaiement)}`, 160, currentY);
  }
  
  return {
    doc,
    totalHT,
    total,
    remise,
    client,
    ipm,
    nom,
    datestring,
    blackColor,
    isIPMType,
    modePaiement,
    startY: currentY + 10
  };
};

// =============================================
// TABLEAU DES PRODUITS - CORRIGÉ
// =============================================

/**
 * ✅ Créer le tableau des produits avec le bon calcul selon le mode TVA
 */
const createGroupedTable = (doc, reportProd, startY, blackColor, isIPMType, modePaiement) => {
  const { FEATURE_TVA_ENABLED } = TVA_CONFIG;
  const groupedProducts = groupProductsByReference(reportProd);
  
  // Définir les colonnes selon le mode TVA
  let tableColumns;
  
  if (FEATURE_TVA_ENABLED) {
    // MODE TVA ACTIVÉ - Afficher TTC
    if (!isIPMType) {
      tableColumns = [
        { header: "Produit", dataKey: "produit", width: 55 },
        { header: "Date", dataKey: "date", width: 18 },
        { header: "Qty", dataKey: "quantite", width: 10 },
        { header: "Prix HT", dataKey: "prixHT", width: 25 },
        { header: "Prix TTC", dataKey: "prixTTC", width: 25 },
        { header: "Total TTC", dataKey: "totalTTC", width: 25 },
        { header: "Bénéf.", dataKey: "beneficiaire", width: 25 }
      ];
    } else {
      tableColumns = [
        { header: "Produit", dataKey: "produit", width: 55 },
        { header: "Date", dataKey: "date", width: 18 },
        { header: "Qty", dataKey: "quantite", width: 10 },
        { header: "Prix HT", dataKey: "prixHT", width: 20 },
        { header: "Prix TTC", dataKey: "prixTTC", width: 20 },
        { header: "Bénéf.", dataKey: "beneficiaire", width: 18 },
        { header: "Part Cli", dataKey: "partClient", width: 20 },
        { header: "Part IPM", dataKey: "partIPM", width: 20 }
      ];
    }
  } else {
    // MODE TVA DÉSACTIVÉ - Afficher HT uniquement
    if (!isIPMType) {
      tableColumns = [
        { header: "Produit", dataKey: "produit", width: 65 },
        { header: "Date", dataKey: "date", width: 18 },
        { header: "Qty", dataKey: "quantite", width: 10 },
        { header: "Prix U.", dataKey: "prixUnitaire", width: 25 },
        { header: "Total", dataKey: "total", width: 25 },
        { header: "Bénéf.", dataKey: "beneficiaire", width: 25 }
      ];
    } else {
      tableColumns = [
        { header: "Produit", dataKey: "produit", width: 50 },
        { header: "Date", dataKey: "date", width: 18 },
        { header: "Qty", dataKey: "quantite", width: 10 },
        { header: "Prix U.", dataKey: "prixUnitaire", width: 20 },
        { header: "Bénéf.", dataKey: "beneficiaire", width: 20 },
        { header: "Part Cli", dataKey: "partClient", width: 20 },
        { header: "Part IPM", dataKey: "partIPM", width: 20 }
      ];
    }
  }

  const allTableRows = [];

  groupedProducts.forEach((group, groupIndex) => {
    // Ligne de groupe
    allTableRows.push([
      {
        content: `Réf: ${group.reference || ''} | Créateur: ${group.createur || ''}`,
        colSpan: tableColumns.length,
        styles: {
          fillColor: [245, 245, 245],
          textColor: blackColor,
          fontStyle: 'bold',
          fontSize: 6,
          halign: 'left',
          cellPadding: 1
        }
      }
    ]);

    // Produits du groupe
    group.produits.forEach((prod) => {
      const quantite = prod.qtVdu || 0;
      const prixHT = prod.prix_sans_tva || prod.prixProdVendu || 0;
      const remisePourcentage = prod.remise || 0;
      const mode = prod.mode_payement || prod.mode || modePaiement;
      
      // ✅ Calcul selon le mode TVA
      const prixUnitaire = calculerPrixUnitaire(prixHT, mode, remisePourcentage);
      const totalLigne = prixUnitaire * quantite;

      const rowData = [];
      
      // Nom du produit
      const nomProd = prod.nomProd || '';
      const maxLength = FEATURE_TVA_ENABLED ? 45 : 55;
      const nomDisplay = nomProd.length > maxLength ? 
        nomProd.substring(0, maxLength - 3) + '...' : nomProd;
      
      rowData.push(
        nomDisplay,
        formatDate(prod.dateEnreg || prod.dateInsert),
        quantite.toString()
      );

      if (FEATURE_TVA_ENABLED) {
        // MODE TVA ACTIVÉ - Afficher HT et TTC
        rowData.push(
          formatPrice(prixHT),
          formatPrice(prixUnitaire), // Prix TTC
          formatPrice(totalLigne),   // Total TTC
          prod.beneficiaire || ''
        );
      } else {
        // MODE TVA DÉSACTIVÉ - Afficher HT seulement
        rowData.push(
          formatPrice(prixUnitaire)  // Prix HT (après remise)
        );
        
        if (!isIPMType) {
          rowData.push(
            formatPrice(totalLigne),  // Total HT
            prod.beneficiaire || ''
          );
        } else {
          rowData.push(
            prod.beneficiaire || '',
            formatPrice(prod.total_client || 0),
            formatPrice(prod.total_ipm || 0)
          );
        }
      }

      allTableRows.push(rowData);
    });

    // Espace entre les groupes
    if (groupIndex < groupedProducts.length - 1) {
      allTableRows.push(new Array(tableColumns.length).fill(''));
    }
  });

  // Configuration des styles de colonnes
  const columnStyles = {};
  tableColumns.forEach((col, index) => {
    columnStyles[index] = { 
      cellWidth: col.width,
      halign: ['prixHT', 'prixTTC', 'totalTTC', 'prixUnitaire', 'total', 'partClient', 'partIPM'].includes(col.dataKey) ? 'right' : 
              ['date', 'quantite'].includes(col.dataKey) ? 'center' : 'left',
      fontSize: 6,
      cellPadding: 1
    };
  });

  const totalWidth = tableColumns.reduce((sum, col) => sum + col.width, 0);
  const leftMargin = 10;
  const rightMargin = 200 - totalWidth;
  
  doc.autoTable({
    startY: startY,
    head: [tableColumns.map(col => col.header)],
    body: allTableRows,
    theme: 'grid',
    styles: {
      fontSize: 6,
      cellPadding: 1,
      lineColor: blackColor,
      lineWidth: 0.1,
      font: 'helvetica',
      textColor: blackColor,
      minCellHeight: 5
    },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: blackColor,
      fontStyle: 'bold',
      fontSize: 6,
      halign: 'center',
      cellPadding: 2,
      lineColor: blackColor,
      lineWidth: 0.2
    },
    bodyStyles: {
      textColor: blackColor,
      cellPadding: 1,
      fontSize: 6
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255]
    },
    columnStyles: columnStyles,
    margin: { left: leftMargin, right: rightMargin },
    tableLineColor: blackColor,
    tableLineWidth: 0.1,
    didParseCell: function(data) {
      if (data.row.raw[0] && typeof data.row.raw[0] === 'object' && data.row.raw[0].content) {
        data.cell.styles = data.row.raw[0].styles;
        data.cell.text = data.row.raw[0].content;
      }
      if (data.column.index === 0 && data.cell.text.length > 20) {
        data.cell.styles.fontSize = 5;
      }
    }
  });

  return doc.lastAutoTable.finalY;
};

// =============================================
// SECTION RÉCAPITULATIVE - CORRIGÉE
// =============================================

/**
 * ✅ Créer la section récapitulative avec le bon calcul selon le mode TVA
 */
const createSummarySection = (doc, reportProd, tableBottom, blackColor, isIPMType, modePaiement, totalHT, total) => {
  const { FEATURE_TVA_ENABLED } = TVA_CONFIG;
  
  let totalHTValue = totalHT || 0;
  let totalValue = total || 0;
  let client = 0, ipm = 0, remise = 0;

  if (!isIPMType) {
    reportProd.forEach(prod => {
      remise = prod.remise || 0;
    });
  } else {
    reportProd.forEach(prod => {
      client += prod.total_client || 0;
      ipm += prod.total_ipm || 0;
    });
    totalHTValue = client + ipm;
    totalValue = totalHTValue; // Pour IPM, on garde HT
  }

  let netAPayer;
  let calculTVA;
  
  if (FEATURE_TVA_ENABLED && !isIPMType) {
    // ✅ MODE TVA ACTIVÉ - Calculer TTC
    calculTVA = calculerTVA(totalHTValue, modePaiement, remise);
    netAPayer = calculTVA.ttc;
  } else {
    // ✅ MODE TVA DÉSACTIVÉ - Utiliser HT
    netAPayer = totalValue;
  }
  
  const pageHeight = doc.internal.pageSize.height;
  const currentY = tableBottom + 5;
  
  if (currentY + 70 > pageHeight) {
    doc.addPage();
    return createSummarySection(doc, reportProd, 20, blackColor, isIPMType, modePaiement, totalHTValue, totalValue);
  }

  const summaryY = currentY;
  let summaryHeight = 28;
  let yOffset = summaryY + 7;

  if (FEATURE_TVA_ENABLED && !isIPMType) summaryHeight += 10;
  if (remise > 0) summaryHeight += 5;
  if (isIPMType) summaryHeight += 10;

  // Cadre du récapitulatif
  doc.setFillColor(250, 250, 250);
  doc.roundedRect(120, summaryY, 75, summaryHeight, 2, 2, 'F');
  doc.setDrawColor(...blackColor);
  doc.setLineWidth(0.3);
  doc.roundedRect(120, summaryY, 75, summaryHeight, 2, 2, 'D');

  // Titre
  doc.setFontSize(8);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'bold');
  doc.text('RÉCAPITULATIF', 125, yOffset);

  yOffset += 5;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  
  if (!isIPMType) {
    // ✅ Afficher Sous-total HT dans tous les cas
    doc.text('Sous-total HT:', 125, yOffset);
    doc.text(formatPrice(totalHTValue), 190, yOffset, { align: 'right' });
    yOffset += 4;
    
    if (remise > 0) {
      doc.text(`Remise (${remise}%):`, 125, yOffset);
      const remiseMontant = (remise * totalHTValue) / 100;
      doc.text(`- ${formatPrice(remiseMontant)}`, 190, yOffset, { align: 'right' });
      yOffset += 4;
      
      if (FEATURE_TVA_ENABLED) {
        const netHT = totalHTValue - remiseMontant;
        doc.text(`Net HT:`, 125, yOffset);
        doc.text(formatPrice(netHT), 190, yOffset, { align: 'right' });
        yOffset += 4;
      }
    }
    
    // ✅ TVA uniquement si activée
    if (FEATURE_TVA_ENABLED && calculTVA) {
      doc.text(`TVA (${formatTauxTVA(modePaiement)}):`, 125, yOffset);
      doc.text(`+ ${formatPrice(calculTVA.tva)}`, 190, yOffset, { align: 'right' });
      yOffset += 4;
    }
  } else {
    doc.text('Part Client HT:', 125, yOffset);
    doc.text(formatPrice(client), 190, yOffset, { align: 'right' });
    yOffset += 4;
    
    doc.text('Part IPM HT:', 125, yOffset);
    doc.text(formatPrice(ipm), 190, yOffset, { align: 'right' });
    yOffset += 4;
  }

  // Ligne de séparation avant le total
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.2);
  doc.line(125, yOffset, 190, yOffset);
  yOffset += 2;

  // ✅ Total selon le mode TVA
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  const labelTotal = FEATURE_TVA_ENABLED && !isIPMType ? 'TOTAL TTC:' : 'TOTAL HT:';
  doc.text(labelTotal, 125, yOffset);
  doc.text(formatPrice(netAPayer), 190, yOffset, { align: 'right' });

  return {
    netAPayer,
    totalHT: totalHTValue,
    totalTTC: netAPayer,
    summaryBottom: summaryY + summaryHeight
  };
};

// =============================================
// FONCTION PRINCIPALE - CORRIGÉE
// =============================================

/**
 * ✅ Fonction principale de génération PDF - CORRIGÉE
 */
const generatePDFBon = (reportProd, clientInfo = null) => {
  // Rafraîchir la config TVA à chaque génération
  TVA_CONFIG = getTVAConfig();
  
  const doc = new jsPDF();
  const isIPMType = clientInfo?.designation?.toUpperCase() === "IPM RIDWAN";
  
  let totalHT = 0, total = 0, remise = 0, numero = '', nom = '';
  
  // ✅ Calcul des totaux selon le mode TVA
  if (!isIPMType) {
    reportProd.forEach(prod => {
      const prixHT = prod.prix_sans_tva || prod.prixProdVendu || 0;
      const quantite = prod.qtVdu || 1;
      const montantHT = prixHT * quantite;
      const mode = prod.mode_payement || prod.mode || 'ESPECE';
      const remiseProd = prod.remise || 0;
      
      totalHT += montantHT;
      
      if (TVA_CONFIG.FEATURE_TVA_ENABLED) {
        // TVA ACTIVÉE - Calculer TTC
        total += calculerTotalLigne(prixHT, quantite, mode, remiseProd);
      } else {
        // TVA DÉSACTIVÉE - Garder HT
        total += montantHT;
      }
      
      remise = prod.remise || remise;
      numero = prod.numFactSrt || numero;
      nom = prod.nom || nom;
    });
  } else {
    console.log("=====================")
    console.log(reportProd.data)
    console.log("=====================")
    reportProd.forEach(prod => {
      const prixHT = prod.prixProdVendu || 0;
      const quantite = prod.qtVdu || 1;
      const montantHT = prixHT * quantite;
      
      totalHT += montantHT;
      total += montantHT; // Pour IPM, toujours HT
      numero = prod.numFactSrt || numero;
      nom = prod.nom || nom;
    });
  }

  const headerResult = createModernHeader(doc, reportProd, clientInfo);
  const tableBottom = createGroupedTable(doc, reportProd, headerResult.startY, headerResult.blackColor, headerResult.isIPMType, headerResult.modePaiement);
  
  const summaryResult = createSummarySection(
    doc, 
    reportProd, 
    tableBottom, 
    headerResult.blackColor, 
    headerResult.isIPMType, 
    headerResult.modePaiement, 
    totalHT, 
    total
  );

  // Pied de page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageHeight = doc.internal.pageSize.height;
    
    if (i === pageCount) {
      let signatureY = summaryResult.summaryBottom + 10;
      if (signatureY + 30 > pageHeight) {
        doc.addPage();
        signatureY = 40;
      }

      doc.setFontSize(6);
      doc.setTextColor(...headerResult.blackColor);
      doc.setFont('helvetica', 'normal');
      try {
        const amountText = amountToWords(summaryResult.totalTTC);
        doc.text(`Arrêtée à la somme de: ${amountText.toUpperCase()}`, 15, signatureY);
      } catch (error) {
        doc.text(`Arrêtée à la somme de: ${formatPriceNoDecimal(summaryResult.totalTTC)}`, 15, signatureY);
      }

      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('Signature et cachet', 160, signatureY + 10, { align: 'center' });
      doc.line(145, signatureY + 13, 175, signatureY + 13);
    }

    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...headerResult.blackColor);
    let infoText = `Page ${i} sur ${pageCount} • ${PHARMACY_NAME}`;
    if (TVA_CONFIG.FEATURE_TVA_ENABLED) {
      infoText += ` • TVA ${formatTauxTVA(headerResult.modePaiement)}`;
    }
    doc.text(infoText, 105, pageHeight - 8, { align: 'center' });
  }

  const d = new Date();
  const datestring = d.getFullYear() + "-" + 
                    ("0" + (d.getMonth() + 1)).slice(-2) + "-" + 
                    ("0" + d.getDate()).slice(-2) + "_" + 
                    ("0" + d.getHours()).slice(-2) + 
                    ("0" + d.getMinutes()).slice(-2);

  const clientName = nom ? nom.replace(/[^a-zA-Z0-9]/g, '_') : 'client';
  doc.save(`Facture_${numero}_${clientName}_${datestring}.pdf`);
};

// =============================================
// EXPORTS
// =============================================

export default generatePDFBon;

export { 
  formatPrice, 
  formatPriceNoDecimal,
  formatDate, 
  amountToWords, 
  groupProductsByReference, 
  createModernHeader, 
  createSummarySection, 
  createGroupedTable,
  calculerTVA,
  formatTauxTVA,
  calculerPrixUnitaire,
  calculerTotalLigne,
  getTVAConfig
};